import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../components/ui/Header';
import Footer from '../components/ui/Footer';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';

const NotFound = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: 'Cloud',
      title: 'Cloud POS',
      description: 'Online store management with real-time sync and multi-store support',
      link: '/',
      color: 'primary'
    },
    {
      icon: 'HardDrive',
      title: 'Desktop POS',
      description: 'Offline-first POS that works without internet, perfect for Nigerian retailers',
      link: '/desktop-app',
      color: 'accent'
    },
    {
      icon: 'Package',
      title: 'Inventory Management',
      description: 'Real-time stock tracking, low stock alerts, and automated reordering',
      link: '/features',
      color: 'secondary'
    },
    {
      icon: 'BarChart3',
      title: 'Analytics & Reports',
      description: 'Comprehensive business intelligence and detailed sales analytics',
      link: '/features',
      color: 'primary'
    }
  ];

  return (
    <>
      <Helmet>
        <title>404 - Page Not Found | Cultaz Retail Management</title>
        <meta name="description" content="Page not found. Discover Cultaz - hybrid retail management software for Nigerian retailers. Cloud POS, offline desktop POS, inventory management, and more." />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="404 - Page Not Found | Cultaz" />
        <meta property="og:description" content="Discover Cultaz - hybrid retail management software for Nigerian retailers." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://cultaz.com/404" />
      </Helmet>
      
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-secondary/5 py-16 md:py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-6 mix-blend-overlay"></div>
          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center lg:text-left"
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="mb-8"
                >
                  <h1 className="text-9xl md:text-[12rem] font-bold text-primary/20 leading-none mb-4">
                    404
                  </h1>
                </motion.div>

                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                  Oops! Page Not Found
                </h2>
                
                <p className="font-body text-lg md:text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                  The page you're looking for doesn't exist, but don't worry! While you're here, discover how Cultaz can transform your retail business.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button
                    asChild
                    variant="default"
                    size="lg"
                    iconName="Home"
                    iconPosition="left"
                    className="w-full sm:w-auto"
                  >
                    <Link to="/">Go to Homepage</Link>
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="ArrowLeft"
                    iconPosition="left"
                    className="w-full sm:w-auto"
                    onClick={() => navigate(-1)}
                  >
                    Go Back
                  </Button>
                </div>

                <div className="flex items-center gap-6 text-sm text-muted-foreground justify-center lg:justify-start">
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" strokeWidth={2} />
                    <span>5,000+ Active Retailers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" strokeWidth={2} />
                    <span>Trusted in Nigeria</span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border">
                  <img
                    src="/cultaz-cloud.png"
                    alt="Cultaz Cloud POS"
                    className="w-full h-auto aspect-[4/3] object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* What We Offer Section */}
        <section className="py-16 md:py-20 lg:py-24 bg-background">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12 md:mb-16"
            >
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                What We Offer
              </h2>
              <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                Hybrid retail management solutions designed for Nigerian retailers. Choose cloud convenience or offline reliability - or use both!
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={service.link}
                    className="block h-full p-6 bg-card rounded-xl border border-border hover:border-primary transition-smooth hover:shadow-purple-md group"
                  >
                    <div className={`flex h-14 w-14 items-center justify-center rounded-xl bg-${service.color}/10 mb-4 group-hover:scale-110 transition-smooth`}>
                      <Icon name={service.icon} size={28} color={`var(--color-${service.color})`} strokeWidth={2} />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                      {service.title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground">
                      {service.description}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Cultaz Section */}
        <section className="py-16 md:py-20 lg:py-24 bg-muted/30">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Why Nigerian Retailers Choose Cultaz
                </h2>
                <p className="font-body text-lg text-muted-foreground mb-8">
                  We understand the unique challenges of retail in Nigeria. That's why we built a hybrid solution that works online and offline.
                </p>

                <div className="space-y-4">
                  {[
                    { icon: 'Zap', text: 'Lightning-fast POS with instant checkout' },
                    { icon: 'Wifi', text: 'Works completely offline - no internet needed' },
                    { icon: 'Shield', text: 'Local data storage with complete control' },
                    { icon: 'BarChart3', text: 'Comprehensive analytics and reporting' },
                    { icon: 'Users', text: 'Multi-user support with role-based permissions' },
                    { icon: 'Package', text: 'Real-time inventory tracking and alerts' }
                  ].map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name={feature.icon} size={20} color="var(--color-primary)" strokeWidth={2} />
                      </div>
                      <p className="font-body text-base text-foreground pt-2">{feature.text}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border">
                  <img
                    src="/cultaz-offline.png"
                    alt="Cultaz Desktop POS"
                    className="w-full h-auto aspect-[4/3] object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary via-secondary to-accent">
          <div className="container mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
                Ready to Transform Your Retail Business?
              </h2>
              <p className="font-body text-lg md:text-xl text-white/90 mb-8 md:mb-10 max-w-2xl mx-auto">
                Join thousands of Nigerian retailers who trust Cultaz for their inventory management and POS needs. Start your free trial today.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6">
                <Button
                  asChild
                  variant="default"
                  size="lg"
                  iconName="Rocket"
                  iconPosition="left"
                  className="bg-white text-primary hover:bg-white/90 w-full sm:w-auto"
                >
                  <Link to="/">Get Started Free</Link>
                </Button>
                
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  iconName="DollarSign"
                  iconPosition="left"
                  className="border-white text-white hover:bg-white/10 w-full sm:w-auto"
                >
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default NotFound;
